import { styled } from '@linaria/react';
import { Flex, Group, Select, Stack, Switch } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useModelComparison } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type { ModelProvider } from '../../../api/agent-miner-client/agent-miner-client.type';
import { ChartBox } from '../../../components/chart-styles/chart-styles';
import { FilterPanel } from '../../../components/filters/filter-panel';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { formatDateRange, STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { useAgentMinerStore } from '../../../store/agent-miner-store/agent-miner-store';
import { AgentMinerTab } from '../agent-miner.type';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import { toDateOrNull, toIsoDateOrNull } from '../sessions-list/filters/session-filters.helpers';
import {
  DATE_OPTIONS,
  type DateRangePreset,
  RESET_KEYS,
  type SessionsFiltersValue,
} from '../sessions-list/filters/session-filters.type';
import { MODEL_COMPARISON_URL_PARAMS, SESSIONS_PANEL_PAGE_SIZE } from '../agent-miner.constants';
import type { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { CycleTimeChart } from './cycle-time-chart';
import {
  MODEL_COMPARISON_ALLOCATION_TYPES,
  filterResponseByProviders,
  filterResponseHidingUnknown,
} from './model-comparison.helpers';
import { SessionListSideSection } from '../shared/session-list-panel/session-list-side-section';
import {
  useModelComparisonAllocationType,
  useModelComparisonHiddenFamilies,
  useModelComparisonParams,
  useModelComparisonProviderFilter,
  useModelComparisonSessionsFamily,
  useModelComparisonShowUnknown,
  useModelComparisonView,
} from './model-comparison.hooks';
import { KpiStrip } from './kpi-strip';
import { ALLOCATION_TYPE_LABELS, MODEL_PROVIDER_LABELS, normalizeProvider } from './model-comparison.type';
import { ModelComparisonTable } from './model-comparison-table';
import { useUsageMeasure } from '../usage/usage.hooks';
import type { UsageMeasure } from '../usage/usage.type';
import { USAGE_MEASURES, UsageView, measureLabel } from '../usage/usage';

const DEFAULT_DATE_PRESET: DateRangePreset = '30d';

// Model Comparison is window-scoped, so "All time" isn't offered on this tab.
const MODEL_COMPARISON_DATE_OPTIONS = DATE_OPTIONS.filter((option) => option.value !== 'all');

/**
 * Overview-slot content for the Model Comparison tab: the four KPI tiles
 * (sessions, median cycle time, throughput, rework rate). Shared across every
 * view (cycle time, token usage, all metrics) so the overview reads the same
 * as the other Agent Miner tabs.
 *
 * @returns {JSX.Element} The KPI strip fed by the shared model comparison query
 */
export const ModelComparisonOverview = () => {
  const params = useModelComparisonParams();
  const { data, query } = useModelComparison(params);

  return <KpiStrip summary={data?.summary} isLoading={query.isPending} />;
};

type ModelComparisonFiltersProps = {
  value: SessionsFiltersValue;
  setDateRange: (value: DateRangePreset) => void;
  setCustomRange: (start: string | null, end: string | null) => void;
  onClose: () => void;
};

/**
 * Side filter panel for the Model Comparison tab: a chart/table view selector
 * at the top and the same date-window control the Sessions view uses (shared
 * URL params, so the range carries across tabs).
 *
 * @param {ModelComparisonFiltersProps} props - Component props
 * @returns {JSX.Element} The filter panel content
 */
export const ModelComparisonFilters = ({
  value,
  setDateRange,
  setCustomRange,
  onClose,
}: ModelComparisonFiltersProps) => {
  const { t } = useTranslation();
  const { view, setView } = useModelComparisonView();
  const { measure, setMeasure } = useUsageMeasure();
  const { providers, setProviders } = useModelComparisonProviderFilter();
  const { showUnknown, setShowUnknown } = useModelComparisonShowUnknown();
  const { hiddenFamilies, setHiddenFamilies } = useModelComparisonHiddenFamilies();
  const { effectiveAllocationType, setAllocationType } = useModelComparisonAllocationType();
  const params = useModelComparisonParams();
  const { data } = useModelComparison(params);
  // Own search-params instance for Reset — see the comment on handleResetFilters
  // below for why this can't compose the individual setters.
  const [searchParams, setSearchParams] = useSearchParams();

  // Distinct providers actually present in the current date window, mirroring
  // how the Sessions tab's source/label options are built from live facets
  // rather than a fixed enum — an org that's never used Gemini shouldn't see
  // an empty "Google" checkbox. Sorted alphabetically by label rather than
  // by session count or union order.
  const providerOptions = useMemo(() => {
    const fromFamilies = new Set((data?.families ?? []).map((family) => normalizeProvider(family.provider)));
    return Array.from(fromFamilies)
      .map((provider) => ({ value: provider, label: MODEL_PROVIDER_LABELS[provider] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.families]);

  const providerFilterGroups: FilterGroup[] = useMemo(
    () =>
      providerOptions.length > 0
        ? [
            {
              name: t('agent_miner.model_comparison.provider'),
              icon: 'smart_toy',
              options: providerOptions,
              selectedValues: providers,
              onSelectionChange: (values) => setProviders(values as ModelProvider[]),
              optional: true,
              multiSelect: true,
            },
          ]
        : [],
    [providerOptions, providers, setProviders, t],
  );

  // Three independent URL-backed setters (date/session filters, view,
  // provider) each snapshot `searchParams` at call time and call
  // `setSearchParams` themselves — calling all three in one handler makes
  // them race off the same stale snapshot and only the last write survives
  // (see BFK-2060 review). Build one combined URLSearchParams and write it
  // in a single call instead.
  const handleResetFilters = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of RESET_KEYS) next.delete(key);
    // Also clear every Model Comparison / Usage-only param — including the
    // drill-down family and window (sessions_family/sessions_start/sessions_end),
    // which the old per-key list missed, so Reset left the drill-down open.
    for (const key of MODEL_COMPARISON_URL_PARAMS) next.delete(key);
    next.delete('session');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  // Select/deselect all chart lines. Operates on the provider-filtered family
  // list — the same set the chart is drawing. Only meaningful in chart view;
  // the control is hidden in table view (the table ignores hidden families).
  const allFamiliesVisible = hiddenFamilies.size === 0;
  const handleToggleAllFamilies = () => {
    if (allFamiliesVisible && data) {
      // Only the families actually drawn (after the provider and hide-Unknown
      // filters) should be swept into the hidden set, so "Deselect all" doesn't
      // pin an already-hidden Unknown family that the chart never showed.
      const chartFamilies = filterResponseHidingUnknown(
        filterResponseByProviders(data, providers),
        showUnknown,
      ).families;
      setHiddenFamilies(new Set(chartFamilies.map((familyEntry) => familyEntry.model_family)));
    } else {
      setHiddenFamilies(new Set());
    }
  };

  const dateSection = (
    <Stack gap={16}>
      {view === 'usage' && (
        <>
          <Group gap="xs">
            <Icon name="data_usage" size={16} color={inkLight} />
            <Text weight="medium" size="small">
              {t('agent_miner.usage.measure_label')}
            </Text>
          </Group>
          <Select
            aria-label={t('agent_miner.usage.measure_label')}
            data={USAGE_MEASURES.map((option) => ({ value: option, label: measureLabel(option, t) }))}
            value={measure}
            onChange={(next) => {
              if (next) setMeasure(next as UsageMeasure);
            }}
            size="sm"
            radius="xl"
            allowDeselect={false}
          />
          <Divider />
        </>
      )}
      <Group gap="xs">
        <Icon name="schedule" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.started')}
        </Text>
      </Group>
      <Select
        data={MODEL_COMPARISON_DATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
        // A stale "All time" from the shared Sessions param shows as the default.
        value={value.dateRange === 'all' ? DEFAULT_DATE_PRESET : value.dateRange}
        onChange={(next) => setDateRange((next ?? DEFAULT_DATE_PRESET) as DateRangePreset)}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      {value.dateRange === 'custom' && (
        <DatePickerInput
          type="range"
          placeholder="Pick dates"
          value={[toDateOrNull(value.customStart), toDateOrNull(value.customEnd)]}
          onChange={(range) => {
            const [start, end] = range as DatesRangeValue;
            setCustomRange(toIsoDateOrNull(start), toIsoDateOrNull(end));
          }}
          valueFormatter={({ date }) => {
            if (!Array.isArray(date)) return '';
            const [start, end] = date;
            if (!start || !end) return '';
            return `${dayjs(start).format('MM/DD/YYYY')} – ${dayjs(end).format('MM/DD/YYYY')}`;
          }}
          maxDate={new Date()}
          size="sm"
          radius="xl"
          clearable
        />
      )}
      <Divider />
      <Group gap="xs">
        <Icon name="date_range" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.model_comparison.allocation_type')}
        </Text>
      </Group>
      <Select
        aria-label={t('agent_miner.model_comparison.allocation_type')}
        data={MODEL_COMPARISON_ALLOCATION_TYPES.map((type) => ({
          value: type,
          label: ALLOCATION_TYPE_LABELS[type],
        }))}
        value={effectiveAllocationType}
        onChange={(next) => {
          if (next) setAllocationType(next as TimeAllocationType);
        }}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      <Divider />
      <Switch
        label={t('agent_miner.model_comparison.show_unknown')}
        checked={showUnknown}
        onChange={(event) => setShowUnknown(event.currentTarget.checked)}
        size="sm"
      />
      {(view === 'chart' || view === 'usage') && (
        <>
          <Divider />
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Icon name="show_chart" size={16} color={inkLight} />
              <Text weight="medium" size="small">
                {t('agent_miner.model_comparison.families_label')}
              </Text>
            </Group>
            <Button size="xs" radius="xl" variant="outline" onClick={handleToggleAllFamilies}>
              {allFamiliesVisible ? t('common.labels.deselect_all') : t('common.labels.select_all')}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );

  return (
    <FilterPanel<typeof view>
      selectedOption={view}
      options={[
        {
          value: 'chart',
          label: t('agent_miner.model_comparison.view_chart'),
          leftSection: <Icon name="show_chart" size={16} color={inkLight} />,
        },
        {
          value: 'usage',
          label: t('agent_miner.model_comparison.view_usage'),
          leftSection: <Icon name="data_usage" size={16} color={inkLight} />,
        },
        {
          value: 'table',
          label: t('agent_miner.model_comparison.view_table'),
          leftSection: <Icon name="table_chart" size={16} color={inkLight} />,
        },
      ]}
      onOptionChange={setView}
      headerSection={dateSection}
      filterGroups={providerFilterGroups}
      onResetFilters={handleResetFilters}
      onClose={onClose}
    />
  );
};

/**
 * Main-section content for the Model Comparison tab: the cycle-time-by-model
 * chart or the comparison table, cycled via the filter panel's view selector.
 * Owns the loading, error, and empty states.
 *
 * @returns {JSX.Element} The chart or table filling the main section
 */
export const ModelComparisonMain = () => {
  const { t } = useTranslation();
  const params = useModelComparisonParams();
  const { view } = useModelComparisonView();
  const { providers } = useModelComparisonProviderFilter();
  const { showUnknown } = useModelComparisonShowUnknown();
  const { data: rawData, query } = useModelComparison(params);
  const { family, setFamily } = useModelComparisonSessionsFamily();
  const { hiddenFamilies, toggleFamily } = useModelComparisonHiddenFamilies();
  // Same org-config + permission gate the sessions surfaces use; combined
  // with the backend's meta.cost_visible so UI and payload agree.
  const showApiCost = useShowAgentMinerApiCost();

  // rawData only changes when the date-window query refetches, so memoizing
  // on it (rather than the parent-scoped `data`) keeps the filtered object's
  // identity stable across re-renders that don't touch the provider
  // selection, letting CycleTimeChart's own memos hit. Computed before the
  // early returns below — hooks can't follow a conditional return.
  const data = useMemo(
    () => (rawData ? filterResponseHidingUnknown(filterResponseByProviders(rawData, providers), showUnknown) : rawData),
    [rawData, providers, showUnknown],
  );

  if (query.isError) {
    return (
      <CenteredState>
        <Icon name="error" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {t('agent_miner.model_comparison.error')}
        </Text>
      </CenteredState>
    );
  }

  if (query.isPending || !rawData || !data) {
    return (
      <CenteredState>
        <Text size="regular" color={inkLight}>
          {t('agent_miner.model_comparison.loading')}
        </Text>
      </CenteredState>
    );
  }

  if (data.rows.length === 0) {
    return (
      <CenteredState>
        <Icon name="insights" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {providers.length > 0
            ? t('agent_miner.model_comparison.empty_filtered')
            : t('agent_miner.model_comparison.empty')}
        </Text>
      </CenteredState>
    );
  }

  if (view === 'usage') {
    // The empty-token-series guard lives inside UsageView, not here: a missing
    // series only blanks the CHART sub-views. The Usage table reads per-row
    // token totals (independently optional from the series), so gating the whole
    // view on the series would hide a renderable table in the FE-deployed-before-
    // BE state where rows carry tokens but the series isn't emitted yet.
    return (
      <RelativePane data-testid="model-comparison-main">
        <BrandedLoadingOverlay visible={query.isFetching} scoped variant="colored" />
        <UsageView response={data} allFamilies={rawData.families} onSelectFamily={setFamily} />
      </RelativePane>
    );
  }

  return (
    <RelativePane data-testid="model-comparison-main">
      <BrandedLoadingOverlay visible={query.isFetching} scoped variant="colored" />
      {view === 'chart' ? (
        <ChartBox style={{ flex: 1, minHeight: 0 }}>
          <CycleTimeChart
            response={data}
            allFamilies={rawData.families}
            onSelectFamily={setFamily}
            hiddenFamilies={hiddenFamilies}
            onToggleFamily={toggleFamily}
          />
        </ChartBox>
      ) : (
        <ModelComparisonTable
          rows={data.rows}
          costVisible={data.meta.cost_visible && showApiCost}
          onSelectFamily={setFamily}
          selectedFamily={family}
        />
      )}
    </RelativePane>
  );
};

/**
 * Side-section content for the Model Comparison tab: the sessions behind the
 * clicked table row. Lists the same sessions the row counted (the backend
 * matches a session's dominant model family), drills into a session on click,
 * and hands off to the full Sessions view on expand.
 *
 * @returns {JSX.Element | null} The sessions side panel, or null when no row is selected
 */
export const ModelComparisonSessionsPanel = () => {
  const { t } = useTranslation();
  const params = useModelComparisonParams();
  const { family, window, setFamily } = useModelComparisonSessionsFamily();
  const [searchParams, setSearchParams] = useSearchParams();
  const setActiveTab = useAgentMinerStore((state) => state.setActiveTab);

  // A chart dot carries the period it represents; a table row doesn't, and
  // falls back to the tab's whole window so the panel's count lines up with
  // that row's `sessions` figure.
  const startDate = window?.start ?? params.start_date;
  const endDate = window?.end ?? params.end_date;

  const listParams = useMemo(
    () => ({
      organization_id: params.organization_id,
      page: 1,
      page_size: SESSIONS_PANEL_PAGE_SIZE,
      start_date: startDate,
      end_date: endDate,
      model_family: family ? [family] : undefined,
      ordering: '-last_turn_at',
    }),
    [params.organization_id, startDate, endDate, family],
  );

  if (!family) return null;

  // Hand the family off to the Sessions tab as a real filter (it shows up as a
  // removable pill there), dropping the Model Comparison-only params so the
  // destination URL carries nothing the Sessions view can't act on.
  const handleExpand = () => {
    // Sessions is the ABSENT-`tab` state, but agent-miner.tsx resolves an
    // absent param by falling back to the STORED tab — which is still Model
    // Comparison here. Without updating the store, deleting `tab` bounces
    // straight back to this tab, so write the store too (mirrors
    // handleSelectTab in agent-miner.tsx).
    setActiveTab(AgentMinerTab.Sessions);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    // Strip the identical Model Comparison / Usage-only set that handleSelectTab
    // uses, so both "go to Sessions" paths leave the same clean URL.
    for (const key of MODEL_COMPARISON_URL_PARAMS) next.delete(key);
    next.set('model_family', family);
    // A dot drill-down is narrower than the tab's range, so pin the Sessions
    // view to that same week — otherwise expanding a single week's panel would
    // silently widen the results back to the full range.
    if (window) {
      next.set('started', 'custom');
      next.set('start_date', window.start);
      next.set('end_date', window.end);
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <SessionListSideSection
      title={family}
      subtitle={formatDateRange(startDate, endDate, { format: STANDARD_DATE_FORMATS.NUMERIC_DATE })}
      listParams={listParams}
      onClose={() => setFamily(null)}
      onExpand={handleExpand}
      emptyLabel={t('agent_miner.session_list.empty_for_model')}
    />
  );
};

/**
 * Centered icon-and-message block used for the loading, error, and empty
 * states.
 *
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {JSX.Element} The centered state block
 */
const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <Flex direction="column" align="center" justify="center" gap={12} mih={280} h="100%">
    {children}
  </Flex>
);

/**
 * Positioning context for the chart/table/usage views so the fetching overlay
 * (`position: absolute`) anchors to this pane instead of the page.
 */
const RelativePane = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;
